import type { Login } from "@/models/Login";
import LoginResult from "@/models/LoginResult";
import { ErrorCode } from "~/enum/ErrorCode";
import ErrorMessages from "~/helpers/ErrorMessages";
import { useRegistrationStore } from "~/stores/registrationStore";
import { navigateTo } from "nuxt/app";
import { useLinkAccountStore } from "~/stores/linkAccountStore";
import { useConfigurationStore } from "~/stores/configurationStore";
import { createSnackbarNotification } from "@frontiers/brink-ui";
import { useJourneyConfigurationStore } from "~/stores/journeyConfigurationStore";
import { UserJourneyService } from "~/services/UserJourneyService";

export class SessionService {
    public static async Login(login: Login): Promise<boolean> {
        let maintainLoginIsLoading = false;
        const registrationStore = useRegistrationStore();
        const linkAccountStore = useLinkAccountStore();
        try {
            const response = await fetch("/api/onboarding-ui/v1/session/login", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(login)
            });

            if (response.ok) {
                const loginResult = new LoginResult(await response.json());
                if (loginResult.LoginResultAction == "1") {
                    if (loginResult.returnUrl) {
                        maintainLoginIsLoading = true;
                        navigateTo(loginResult.returnUrl, { external: true });
                    } else {
                        throw new Error("No returnUrl found.");
                    }
                }
            }
            else {
                const errorResult = await response.json();
                const errorCode = errorResult.Code;

                if (errorCode == ErrorCode.AccountInactiveOrNotVerified) {
                    const userJourney = await UserJourneyService.getUserJourneyByUserId(errorResult.Data.UserId);
                    if (userJourney?.journeyId && userJourney.journeyId > 0) {
                        const journeyConfigurationStore = useJourneyConfigurationStore();
                        await journeyConfigurationStore.fetchData(userJourney.journeyId);
                    }
                    registrationStore.verificationEmail = login.email;
                    navigateTo("/register/code-validation");
                }
                else if (errorCode == ErrorCode.UserNotLinkedToTenant) {
                    const loginAuthenticationToken = errorResult.Data.LoginAuthenticationToken;
                    linkAccountStore.setLoginAuthenticationToken(loginAuthenticationToken);
                    navigateTo("/register/link-account");
                }
                else {
                    const errorMessage = ErrorMessages.getMessage(errorCode);
                    if (errorCode === ErrorCode.UserSuspended) {
                        const configurationStore = useConfigurationStore();
                        const configuration = configurationStore.config;
                        createSnackbarNotification({
                            message: errorMessage,
                            type: "error",
                            autoDismiss: true,
                            showCloseIcon: true,
                            actionLabel: "Contact support team",
                            onActionClick: () => {
                                window.location.href = `mailto:${configuration.domainProperties.supportEmail}`;
                            }
                        });
                    }
                    else {
                        createSnackbarNotification({
                            message: errorMessage,
                            type: "error",
                            autoDismiss: true,
                            showCloseIcon: true
                        });
                    }
                }
            }
            return maintainLoginIsLoading;
        } catch (e) {
            console.error("Error posting login: " + e);
            throw e;
        }
    }

    public static async getUserId(): Promise<string | undefined> {
        try {
            const response = await fetch("/api/onboarding-ui/v1/session/userId");
            if (response.ok) {
                const userId = await response.text();
                return userId;
            }
            else {
                const errorResult = await response.json();
                const errorMsg = errorResult.Message;
                throw new Error(errorMsg);
            }
        } catch (e) {
            console.warn("UserId could not be retrieved: " + e);
            return undefined;
        }
    }

    public static async Logout(returnUrl: string | null) {
        try {
            var path = '/api/onboarding-ui/v1/session/logout';

            if (returnUrl != null) {
                path = path + '?returnUrl=' + returnUrl
            }

            const response = await fetch(path, {
                method: "GET"
            });

            if (response.ok) {
                const logoutUrl = await response.text();
                navigateTo(logoutUrl, { external: true });
            }
            else {
            }
        } catch (e) {
            console.error("Error posting login: " + e);
            throw e;
        }
    }
}